import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField} from "@mui/material";
import {enqueueSnackbar} from "notistack";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {NoShowByMailService} from "crqlar-react-common/dist/services/public/noShowByMail.service";
import {
    GuestCount,
    Reservation,
    SlotType
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    AgeCategory,
    AgeCategoryType
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import ReservationDetailsRestaurant
    from "./specializations/ReservationDetailsRestaurant";
import ReservationProfiles from "./ReservationProfiles";
import {
    useService,
    useServicesForSector
} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {
    useAgeCategories,
    useAllAgeCategories
} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {
    useCurrentApp
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import ReservationDetailsService from "./specializations/ReservationDetailsService";
import ReservationDetailsAccommodation
    from "./specializations/ReservationDetailsAccommodation";
import {
    ReservationGroup
} from "crqlar-react-common/dist/models/reservation/reservationGroup.model";
import ReservationGroupInfo from "./ReservationGroupInfo";
import {
    ReservationService
} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {
    ReservationGroupService
} from "crqlar-react-common/dist/services/reservation/reservationGroup.service";
import ReservationFoliosDialog from "./folio/ReservationFolioDialog";
import ReservationCommunicationDialog from "./ReservationCommunicationDialog";
import {useMutation, useQuery} from "react-query";
import {
    AvailabilityService
} from "crqlar-react-common/dist/services/availability/availability.service";
import {
    useAvailabilityCalculationResults
} from "crqlar-react-common/dist/utils/hooks/availability/useResourceAvailability";
import {endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {
    AvailabilityType
} from "crqlar-react-common/dist/models/availability/availability.model";
import {
    AvailabilityBlockRequest
} from "crqlar-react-common/dist/models/bookingEngine/availabilityRequest";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {
    RateCalculationService
} from "crqlar-react-common/dist/services/rate/rateCalculation.service";
import ReservationLogsDialog from "./ReservationLogsDialog";
import {SortDTO} from "crqlar-react-common/dist/models/common/limit";
import SimpleDialog from "crqlar-react-common/dist/components/SimpleDialog";
import ReservationFormSelectorDialog from "./ReservationFormSelectorDialog";
import {
    AvailabilityCalculationResult
} from "crqlar-react-common/dist/models/availability/availabilityCalculationResult.model";
import {isReservationOutsideOpenAvailability} from "../../../utils/reservation.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {thinScrollbarSx} from "crqlar-react-common/dist/utils/scrollbar.util";
import {
    mapGuestCountsToServiceAgeCategories,
    resolveDefaultAgeCategories,
} from "../../common/ageCategoryDefaults";
import {
    useAllChildResources,
    useAllRootResourcesForSector
} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {applyProfileStayToDraft} from "./profileStay/profileStay.util";
import {useProfileStayContext} from "./profileStay/useProfileStayContext";
import {usePackageGroup} from "../packages/packageReservation.util";
import {getPrimaryEmail} from "crqlar-react-common/dist/utils/profile.util";

interface Props {
    open: boolean,
    onClose: () => void,
    reservation?: Reservation,
    onSave: (reservation?: Reservation) => void,
    onChangeReservation?: (reservation: Reservation) => void
    onSaveBlock?: () => void
    linkedReservationsParam?: Reservation[]
    customSort?: SortDTO[] | undefined
    defaultMultidayUntil?: Date
    defaultAllowNotify?: boolean
    defaultExpandedProfile?: Profile
    // Calculated OPEN/CLOSED availability blocks for the calendar's resources. Used to warn when a
    // new reservation is being created outside of the open availability section of its resource.
    availabilityResults?: AvailabilityCalculationResult[]
}

const ReservationDialog = ({
                               open,
                               onClose,
                               reservation,
                               onSave,
                               onSaveBlock,
                               onChangeReservation,
                               linkedReservationsParam,
                               customSort,
                               defaultMultidayUntil,
                               defaultAllowNotify,
                               defaultExpandedProfile,
                               availabilityResults
                           }: Props) => {

    const {t} = useTranslation()
    // Read-only users may open and view a reservation, but not change it — disable every
    // autocomplete in the dialog (service, resource, table, profile search, ...).
    const readOnly = useReadOnly()
    const [tmpReservation, setTmpReservation] = useState<Reservation>()
    const isPackageMember = !!usePackageGroup(tmpReservation?.reservationGroupId).packageService
    const [chosenProfile, setChosenProfile] = useState<Profile>()
    // Guest the form is currently calculated for. Set only by an explicit pick in the guest list -
    // a group booked into several rooms has several stays, and the dialog must follow the one the
    // user selected instead of the group's first reservation.
    const [selectedProfileId, setSelectedProfileId] = useState<string>()
    // const [openSaveDialog, setOpenSaveDialog] = useState(false)
    const [openResendDialog, setOpenResendDialog] = useState(false)
    const serviceInfo = useService(tmpReservation?.serviceId)
    const ageCategories = useAgeCategories(serviceInfo?.ageCategoryIds || [])
    const allAgeCategories = useAllAgeCategories()
    const restaurantAgeCategories = useMemo(() => {
        const reservationCategoryIds = new Set(
            (tmpReservation?.displayGuestCounts ?? tmpReservation?.guestCounts ?? [])
                .map(guestCount => guestCount.ageCategoryId)
                .filter((id): id is string => Boolean(id))
        )
        const ageCategoriesById = new Map(
            allAgeCategories.flatMap(category => category.id
                ? [[category.id, category] as const]
                : [])
        )
        const reservationAgeCategories = Array.from(reservationCategoryIds).map(
            id => ageCategoriesById.get(id) ?? {id, name: id}
        )
        const relevantAgeCategories = [
            ...ageCategories,
            ...resolveDefaultAgeCategories(allAgeCategories),
            ...reservationAgeCategories,
        ]
        const uniqueAgeCategories = new Map(
            relevantAgeCategories.flatMap(category => category.id
                ? [[category.id, category] as const]
                : [])
        )

        return Array.from(uniqueAgeCategories.values())
    }, [ageCategories, allAgeCategories, tmpReservation?.displayGuestCounts, tmpReservation?.guestCounts])
    // Tracks which profile has already been synced so we fetch at most once per dialog session
    const syncedProfileRef = useRef<string | null>(null)
    const savingReservationRef = useRef(false)
    const appData = useCurrentApp()
    // Existing reservations carry their owning sector. Prefer it over the currently
    // active app so reservations opened from cross-sector surfaces (for example CRM)
    // still render the correct specialization.
    const sector = useSector(tmpReservation?.sectorId ?? reservation?.sectorId ?? appData?.configuration?.sectorId)
    const [showFolios, setShowFolios] = useState(false)
    const [showCommunication, setShowCommunication] = useState(false)
    const [showLogs, setShowLogs] = useState(false)

    const [savingReservation, setSavingReservation] = useState(false)
    const [showFormSelector, setShowFormSelector] = useState(false)

    const [noEmailDialogOpen, setNoEmailDialogOpen] = useState(false)
    const [newEmail, setNewEmail] = useState('')
    const [confirmNoShowDialogOpen, setConfirmNoShowDialogOpen] = useState(false)
    const [confirmNoShowEmail, setConfirmNoShowEmail] = useState<string | null>(null)

    const [reservationGroup, setReservationGroup] = useState<ReservationGroup>()
    const [linkedReservations, setLinkedReservations] = useState<Reservation[]>([])

    // Defer mounting the (very heavy) form subtree until the open animation has finished, and
    // unmount it immediately on close. This keeps the open/close transition smooth instead of
    // building/tearing down ~2000 lines of form components in the same frame as the animation.
    const [contentReady, setContentReady] = useState(false)
    useEffect(() => {
        if (!open) {
            setContentReady(false)
            return
        }
        // Backstop in case the transition's onEntered doesn't fire (e.g. reduced-motion / no transition)
        const id = window.setTimeout(() => setContentReady(true), 350)
        return () => window.clearTimeout(id)
    }, [open])

    useEffect(() => {
        if(linkedReservationsParam) {
            setLinkedReservations(linkedReservationsParam)
        } else {
            setLinkedReservations([])
        }
    }, [linkedReservationsParam])

    // Syncs guest counts on the new reservation form based on profile changes:
    //   - profile removed              → reset counts to []
    //   - profile with no active/upcoming stay → reset counts to []
    //   - profile with currentStay or upcomingStays → fill from that accommodation reservation
    // syncedProfileRef prevents duplicate fetches when other deps re-render after the sync.
    const firstProfileId = tmpReservation?.primaryProfileId || tmpReservation?.profileIds?.[0]
    useEffect(() => {
        if (tmpReservation?.id) return  // never auto-assign on existing reservations

        // Profile removed → reset
        if (!firstProfileId) {
            setTmpReservation(prev => {
                if (!prev || prev.id) return prev
                return {...prev, guestCounts: []}
            })
            syncedProfileRef.current = null
            return
        }

        if (!ageCategories.length || !allAgeCategories.length) return
        if (syncedProfileRef.current === firstProfileId) return

        // Lock immediately so re-renders while the fetch is in-flight don't launch duplicate calls
        syncedProfileRef.current = firstProfileId

        ProfileService.getProfileById(firstProfileId).then(profile => {
            // currentStay = checked-in now; upcomingStays[0] = next confirmed arrival
            const stay = profile.currentStay ?? profile.upcomingStays?.[0]
            if (!stay?.reservationId) return undefined
            return ReservationService.getReservationById(stay.reservationId)
        }).then(accomReservation => {
            // No active/upcoming stay → reset to 0
            if (!accomReservation?.guestCounts?.length) {
                setTmpReservation(prev => {
                    if (!prev || prev.id) return prev
                    return {...prev, guestCounts: []}
                })
                return
            }

            // Has stay → translate hotel age-category counts to restaurant age-category ids
            setTmpReservation(prev => {
                if (!prev || prev.id) return prev

                const newGuestCounts = mapGuestCountsToServiceAgeCategories(
                    accomReservation.guestCounts!,
                    ageCategories,
                    allAgeCategories
                )

                return {...prev, guestCounts: newGuestCounts}
            })
        }).catch(() => {/* ref already locked above; no retry on error */})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstProfileId, ageCategories, allAgeCategories])

    // New drafts may contain counts copied from another sector or from a previously selected
    // service. Existing reservations keep their exact stored categories so the form can show
    // and edit every category already present on the reservation.
    useEffect(() => {
        if (!ageCategories.length || !allAgeCategories.length) return

        setTmpReservation(prev => {
            if (!prev) return prev
            if (prev.id) return prev
            if (!prev.guestCounts?.length) return prev

            const newGuestCounts = mapGuestCountsToServiceAgeCategories(
                prev.guestCounts,
                ageCategories,
                allAgeCategories
            )
            if (newGuestCounts === prev.guestCounts) return prev

            return { ...prev, guestCounts: newGuestCounts }
        })
    }, [ageCategories, allAgeCategories, tmpReservation?.id])

    // ---------------------------------------------------------------------------------------
    // Profile-change handler
    //
    // Guests of one reservation group do not share a stay. A booking spread over rooms 22, 24, 61
    // and 84 can have rooms 22/24 checking out on the 23rd and rooms 61/84 on the 24th, and the
    // guests can be split across restaurants. Whenever a guest is picked in the list on the left,
    // the whole form is recalculated from THAT guest's own accommodation reservation: guest
    // counts, restaurant, notes/event, the check-in/check-out shown above the End Date, and - most
    // importantly - the end of the serial (multiday) series.
    // ---------------------------------------------------------------------------------------
    const restaurants = useAllRootResourcesForSector(
        sector?.sectorType === SectorType.RESTAURANT ? sector?.id : undefined
    )
    const restaurantIds = useMemo(
        () => restaurants.map(restaurant => restaurant.id!).filter(Boolean),
        [restaurants]
    )
    const restaurantTables = useAllChildResources(restaurantIds)

    const {profileStayContext} = useProfileStayContext({
        profileId: selectedProfileId,
        restaurantSectorId: sector?.id,
        restaurantIds,
        referenceDate: tmpReservation?.expectedStartDate,
        enabled: open && sector?.sectorType === SectorType.RESTAURANT,
    })

    // Guards against re-applying the same guest on every unrelated re-render, which would undo
    // edits the user made after the recalculation.
    const appliedProfileStayRef = useRef<string | null>(null)

    useEffect(() => {
        if (!open) {
            appliedProfileStayRef.current = null
            return
        }
        if (!profileStayContext) return
        if (appliedProfileStayRef.current === profileStayContext.profileId) return

        appliedProfileStayRef.current = profileStayContext.profileId

        setTmpReservation(prev => {
            // Saved reservations keep what they were saved with - picking a guest only changes
            // what the dialog *shows* for them (check-in/check-out), never the stored booking.
            if (!prev || prev.id) return prev

            return applyProfileStayToDraft(prev, profileStayContext, {
                serviceAgeCategories: ageCategories,
                allAgeCategories,
                restaurants,
                tables: restaurantTables,
            })
        })
    }, [open, profileStayContext, ageCategories, allAgeCategories, restaurants, restaurantTables])

    // The serial series must end on the check-out date of the guest the form is calculated for.
    const effectiveMultidayUntil = profileStayContext?.checkOutDate ?? defaultMultidayUntil

    const handleProfileSelected = useCallback((profileId: string) => {
        setSelectedProfileId(profileId)
    }, [])

    const handleUpdateLinkedReservationTable = useCallback((index: number, newResourceIds: string[]) => {
        setLinkedReservations(prev => prev.map((r, i) =>
            i === index ? {...r, resourceIds: newResourceIds} : r
        ))
    }, [])

    const [selectedSlotType, setSelectedSlotType] = useState<SlotType>(SlotType.RESERVATION)

    const handleSlotTypeChange = (slotType: SlotType) => {
        if (slotType !== SlotType.SERIAL) {
            setLinkedReservations([])
        }
        setSelectedSlotType(slotType)
    }

    const services = useServicesForSector(sector?.id)

    const rateIds = services.flatMap(s => s.rateIds || []) || []

    // The API rejects a range without both ends (400 at 'range.start'). The closed dialog,
    // mounted on every calendar and list, has no reservation yet, so it must not ask.
    const ratesQ = useQuery(["rate", rateIds, tmpReservation?.expectedStartDate, tmpReservation?.expectedEndDate], async () => {
        if (!rateIds?.length) return []
        return RateCalculationService.calculate({
            ids: rateIds,
            range: {
                start: tmpReservation?.expectedStartDate,
                end: tmpReservation?.expectedEndDate
            }
        });
    }, {
        enabled: !!tmpReservation?.expectedStartDate && !!tmpReservation?.expectedEndDate
    })

    const rates = ratesQ.data || []

    const addAvailabilityMut = useMutation(
        async (availabilityBlockRequest: AvailabilityBlockRequest) => {
            await AvailabilityService.createAvailability({
                entityId: tmpReservation?.resourceIds?.[0],
                startTime: tmpReservation?.expectedStartDate,
                endTime: tmpReservation?.expectedEndDate,
                type: availabilityBlockRequest.type,
                color: availabilityBlockRequest.color,
                name: availabilityBlockRequest.name
            })
        }
    )

    // Reset selected slot type on open; default to SERIAL when defaultMultidayUntil is pre-populated
    useEffect(() => {
        if (open) {
            setSelectedSlotType(defaultMultidayUntil ? SlotType.SERIAL : SlotType.RESERVATION)
        }
    }, [open])

    // Auto-detect SERIAL for existing restaurant reservations that belong to a multiday group
    useEffect(() => {
        if (
            sector?.sectorType === SectorType.RESTAURANT &&
            linkedReservations.length > 0 &&
            reservation?.id
        ) {
            setSelectedSlotType(SlotType.SERIAL)
        }
    }, [linkedReservations, sector, reservation?.id])

    useEffect(() => {
        // setOpenSaveDialog(false)
        setShowFolios(false)
        setChosenProfile(defaultExpandedProfile ?? undefined)
        setSelectedProfileId(undefined)
        appliedProfileStayRef.current = null
        setTmpReservation(reservation)
        setLinkedReservations([])
        // A new draft can arrive with guest counts already resolved — e.g. prefilled from the
        // restaurant arrivals list, where the count reflects the whole reservation group's total
        // (3 adults), not the single accommodation reservation (1 adult). Seed the synced-profile
        // ref with that draft's profile so the currentStay sync effect below treats it as
        // "already synced" and does NOT overwrite those counts with the single stay's counts.
        // Changing the profile afterwards still re-derives from the newly chosen guest's stay.
        const prefilledProfileId = reservation?.primaryProfileId || reservation?.profileIds?.[0]
        syncedProfileRef.current = !reservation?.id && reservation?.guestCounts?.length && prefilledProfileId
            ? prefilledProfileId
            : null

        if (reservation?.reservationGroupId) {
            const getLinkedReservations = async () => {
                const linked = await ReservationService.getAllReservations({
                    reservationGroupId: [reservation.reservationGroupId!],
                    limit: {
                        offset: 0,
                        limit: 100,
                        // Introducing custom sort for cases like multiday reservations where order matters
                        sort: customSort
                    }
                })
                setLinkedReservations(linked.content || [])
            }
            getLinkedReservations()
        }

    }, [open, reservation?.id]);

    const onShowFolios = () => {
        setShowFolios(true)
    }

    const onShowLogs = () => {
        setShowLogs(true)
    }

    const onOpenCommunication = () => {
        setShowCommunication(true)
    }

    const onOpenFormSelector = () => {
        setShowFormSelector(true)
    }

    const handleSave = async (
        allowNotify?: boolean,
        updateOption?: 'single' | 'linkedReservations' | 'subsequentLinkedReservations',
        changes?: {
            field: string
            oldValue: any
            newValue: any
        }[],
        eatPeriod?: 'breakfast' | 'lunch' | 'dinner' | 'all' | undefined
    ) => {
        if (!tmpReservation || savingReservationRef.current) return

        savingReservationRef.current = true
        setSavingReservation(true)

        try {
            // We can also receive the linked reservations as parameter (breakfast + dinner option)
            const finalLinkedReservations = linkedReservations.length
                ? linkedReservations
                : linkedReservationsParam || []

            // CREATE
            if (!tmpReservation.id) {
                if (selectedSlotType === SlotType.SERIAL && finalLinkedReservations.length > 1) {
                    const group = await ReservationGroupService.createReservationGroup({})

                    const createdReservations = []
                    for (const linkedReservation of finalLinkedReservations) {
                        // Serial reservations must never trigger transactional emails — suppress
                        // explicitly so a series of bookings can't spam the guest.
                        const createdReservation = await ReservationService.createReservation({
                            ...linkedReservation,
                            reservationGroupId: group.id
                        }, false)
                        createdReservations.push(createdReservation)
                    }

                    await ReservationGroupService.updateReservationGroup({
                        ...group,
                        reservationIds: createdReservations.map(r => r.id!)
                    })

                } else if (selectedSlotType === SlotType.SERIAL && finalLinkedReservations.length === 1) {
                    // A series trimmed down to a single sitting - a guest whose stay only covers
                    // one day of the picked range. Create that sitting, not the untrimmed draft.
                    await ReservationService.createReservation(finalLinkedReservations[0], allowNotify)
                } else {
                    await ReservationService.createReservation(tmpReservation, allowNotify)
                }
            } else {
                // UPDATE

                const dateFieldsToPreserveDate = ['expectedStartDate', 'expectedEndDate', 'actualStartDate', 'actualEndDate']

                if(updateOption === 'linkedReservations') {

                    // Condition 1: Select all linked reservations with the passed eatPeriod (breakfast, lunch, dinner) if provided
                    const finalLinkedWithSelectedEatPeriod = eatPeriod === 'all'
                        ? finalLinkedReservations
                        : finalLinkedReservations.filter(r => r.meta?.eatPeriod === eatPeriod)

                    // Update all linked reservations in the group
                    for (const linkedReservation of finalLinkedWithSelectedEatPeriod) {
                        const updatedReservation = {
                            ...linkedReservation
                        }

                        changes?.forEach(change => {
                            // Check if this is a date field where we only want to update the time

                            if (dateFieldsToPreserveDate.includes(change.field)) {
                                // Keep the original date, only update the time
                                const oldDateValue = (updatedReservation as any)[change.field]
                                const oldDate = new Date(oldDateValue)
                                const newDate = new Date(change.newValue)

                                // Preserve date components from old value, take time components from new value
                                const updatedDate = new Date(
                                    oldDate.getFullYear(),
                                    oldDate.getMonth(),
                                    oldDate.getDate(),
                                    newDate.getHours(),
                                    newDate.getMinutes(),
                                    newDate.getSeconds()
                                );

                                (updatedReservation as any)[change.field] = updatedDate
                            } else {
                                // For non-date fields, simply update the value
                                (updatedReservation as any)[change.field] = change.newValue
                            }
                        })

                        await ReservationService.updateReservation(updatedReservation, allowNotify)
                    }
                } else if(updateOption === 'subsequentLinkedReservations') {
                    // Condition 1: Select reservations with expectedStartDay greater than or equal to the current one
                    // Condition 2: Select only those with the passed eatPeriod (breakfast, lunch, dinner) if provided
                    const subsequentReservations = finalLinkedReservations.filter(r => {
                        return (r.expectedStartDay || '') >= (tmpReservation.expectedStartDay || '') &&
                            (eatPeriod === 'all' || r.meta?.eatPeriod === eatPeriod)
                    })

                    for(const linkedReservation of subsequentReservations) {
                        const updatedReservation = {
                            ...linkedReservation
                        }

                        // Apply changes
                        changes?.forEach(change => {
                            // Check if this is a date field where we only want to update the time

                            if (dateFieldsToPreserveDate.includes(change.field)) {
                                // Keep the original date, only update the time
                                const oldDateValue = (updatedReservation as any)[change.field]
                                const oldDate = new Date(oldDateValue)
                                const newDate = new Date(change.newValue)

                                // Preserve date components from old value, take time components from new value
                                const updatedDate = new Date(
                                    oldDate.getFullYear(),
                                    oldDate.getMonth(),
                                    oldDate.getDate(),
                                    newDate.getHours(),
                                    newDate.getMinutes(),
                                    newDate.getSeconds()
                                );

                                (updatedReservation as any)[change.field] = updatedDate
                            } else {
                                // For non-date fields, simply update the value
                                (updatedReservation as any)[change.field] = change.newValue
                            }
                        })

                        await ReservationService.updateReservation(updatedReservation, allowNotify)
                    }

                } else {
                    // Just single reservation update
                    await ReservationService.updateReservation(tmpReservation, allowNotify)
                }
            }

            onSave(tmpReservation)
        } finally {
            savingReservationRef.current = false
            setSavingReservation(false)
        }
    }

    const handleSaveAndSendNoShowLink = async () => {
        if (!tmpReservation) return;

        const profileId = tmpReservation.primaryProfileId || tmpReservation.profileIds?.[0];
        if (profileId) {
            try {
                const profile = await ProfileService.getProfileById(profileId);
                // Same address the backend mails to (ProfileV3.primaryEmail()).
                const primaryEmail = getPrimaryEmail(profile);
                if (!primaryEmail) {
                    setNoEmailDialogOpen(true);
                    return;
                }
                setConfirmNoShowEmail(primaryEmail);
            } catch {
                setConfirmNoShowEmail(null);
            }
        } else {
            setConfirmNoShowEmail(null);
        }

        setConfirmNoShowDialogOpen(true);
    };

    // The link goes out for a reservation that exists: a draft is created first (silently), while a
    // saved one - a serial sitting, sent from the "..." menu - is used as stored.
    const reservationForNoShowLink = async (): Promise<Reservation | undefined> => {
        if (!tmpReservation) return;
        if (tmpReservation.id) return tmpReservation;
        try {
            return await ReservationService.createReservation(tmpReservation);
        } catch {
            return;
        }
    };

    const handleConfirmedSendNoShowLink = async () => {
        setConfirmNoShowDialogOpen(false);

        const createdReservation = await reservationForNoShowLink();
        if (!createdReservation) return;

        onSave(createdReservation);

        try {
            await NoShowByMailService.sendNoShowLink(createdReservation.id!);
            enqueueSnackbar(t('No-show link sent successfully'), {variant: 'success'});
        } catch (error: any) {
            const message = error?.response?.data?.message;
            enqueueSnackbar(t(message) || t('Could not send no-show link'), {variant: 'error'});
        }
    };

    const handleAddEmailAndSend = async () => {
        if (!newEmail.trim() || !tmpReservation) return;

        const profileId = tmpReservation.primaryProfileId || tmpReservation.profileIds?.[0];
        if (!profileId) return;

        try {
            const profile = await ProfileService.getProfileById(profileId);
            const updatedProfile = {
                ...profile,
                emails: [...(profile.emails || []), {email: newEmail.trim(), isPrimary: true}]
            };
            await ProfileService.updateProfile(updatedProfile);
        } catch {
            enqueueSnackbar(t('Could not update email'), {variant: 'error'});
            return;
        }

        setNoEmailDialogOpen(false);
        setNewEmail('');

        const createdReservation = await reservationForNoShowLink();
        if (!createdReservation) return;

        onSave(createdReservation);

        try {
            await NoShowByMailService.sendNoShowLink(createdReservation.id!);
            enqueueSnackbar(t('No-show link sent successfully'), {variant: 'success'});
        } catch (error: any) {
            const message = error?.response?.data?.message;
            enqueueSnackbar(t(message) || t('Could not send no-show link'), {variant: 'error'});
        }
    };

    const handleSaveBlock = async (name: string | undefined) => {
        try {
            await addAvailabilityMut.mutateAsync({
                type: AvailabilityType.CLOSED,
                color: CommonThemeColors.sys.availability.boldRed,
                name: name || 'Block'
            })

            // Only execute after the mutation is successful
            onSaveBlock?.()
        } catch (error) {
            // Handle error if needed
            console.error('Failed to save block:', error)
        }
    }

    const handleCreateProfile = (profile: Profile) => {
        // When creating new profile from reservation dialog, we need to add it to the reservation
        const newProfileIds = [...(tmpReservation?.profileIds || []), profile.id!]

        // Update the reservation with the new profile IDs
        const updatedReservation = {
            ...tmpReservation!,
            profileIds: newProfileIds
        }

        setTmpReservation(updatedReservation)
    }

    const renderContent = () => {
        return (
            <Stack
                direction={{xs: 'column', md: 'row'}}
                width={'100%'}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowX: 'hidden',
                    overflowY: {xs: 'auto', md: 'hidden'},
                }}
            >
                {
                    // Show profiles tab for all reservation-mode slot types (not block)
                    selectedSlotType !== SlotType.BLOCK && tmpReservation &&
                    <Stack sx={{order: {xs: 1, md: 1}, flex: chosenProfile ? 1 : 'initial', minWidth: 0, overflow: {xs: 'visible', md: 'hidden'}}}>
                        <ReservationProfiles
                            // Trick to rerender the whole child component so that it includes the newly added profile
                            key={tmpReservation?.profileIds?.length || 0}
                            reservation={tmpReservation}
                            setReservation={res => {
                                setTmpReservation(res)
                            }}
                            fullProfilePreview={chosenProfile}
                            setFullProfilePreview={setChosenProfile}
                            open={open}
                            onCreateProfile={handleCreateProfile}
                            onProfileSelected={handleProfileSelected}
                            onClose={onClose}
                        />
                    </Stack>
                }


                {
                    !chosenProfile && sector?.sectorType === SectorType.RESTAURANT &&
                    <Stack sx={{order: {xs: 2, md: 2}, flex: 1, overflowY: {xs: 'initial', md: 'auto'}, ...thinScrollbarSx}}>
                        <ReservationDetailsRestaurant
                            reservation={tmpReservation}
                            setReservation={setTmpReservation}
                            availabilityWarning={availabilityWarning}
                            // Hidden for now on purpose
                            // availabilityForActiveReservation={availabilityForActiveReservation}
                            // availabilityQuery={availabilityQuery.isLoading}
                            onSave={(allowNotify, updateOption, changes, eatPeriod) => {
                                // setOpenSaveDialog(true)
                                handleSave(allowNotify, updateOption, changes, eatPeriod)
                            }}
                            onSaveBlock={handleSaveBlock}
                            onResend={() => {
                                setOpenResendDialog(true)
                            }}
                            ageCategories={restaurantAgeCategories}
                            linkedReservations={
                                // Needed for multiday reservations
                                linkedReservations.length
                                    ? linkedReservations
                                    : linkedReservationsParam
                            }
                            setLinkedReservations={setLinkedReservations}
                            reservationGroup={reservationGroup}
                            setReservationGroup={setReservationGroup}
                            onClose={onClose}
                            onShowFolios={onShowFolios}
                            onShowLogs={onShowLogs}
                            onOpenCommunication={onOpenCommunication}
                            onOpenFormSelector={onOpenFormSelector}
                            serviceInfo={serviceInfo}
                            originalReservation={reservation}
                            rates={rates}
                            defaultMultidayUntil={effectiveMultidayUntil}
                            stayCheckInDate={profileStayContext?.checkInDate}
                            stayCheckOutDate={profileStayContext?.checkOutDate}
                            defaultAllowNotify={defaultAllowNotify}
                            selectedSlotType={selectedSlotType}
                            loading={savingReservation}
                            setSelectedSlotType={handleSlotTypeChange}
                            onSaveAndSendNoShowLink={handleSaveAndSendNoShowLink}
                        />
                    </Stack>
                }

                {
                    !chosenProfile && (
                        sector?.sectorType === SectorType.SPA ||
                        sector?.sectorType === SectorType.OTHER ||
                        sector?.sectorType === SectorType.WEEKLY_PLANNER
                    ) &&
                    <Stack sx={{order: {xs: 2, md: 2}, flex: 1, overflowY: {xs: 'initial', md: 'auto'}, ...thinScrollbarSx}}>
                        <ReservationDetailsService
                            reservation={tmpReservation}
                            setReservation={setTmpReservation}
                            availabilityWarning={availabilityWarning}
                            // Hidden for now on purpose
                            // availabilityForActiveReservation={availabilityForActiveReservation}r
                            // availabilityQuery={availabilityQuery.isLoading}
                            onSave={(allowNotify) => {
                                // setOpenSaveDialog(true)
                                handleSave(allowNotify)
                            }}
                            onSaveBlock={handleSaveBlock}
                            onResend={() => {
                                setOpenResendDialog(true)
                            }}
                            ageCategories={ageCategories}
                            onClose={onClose}
                            onShowFolios={onShowFolios}
                            onShowLogs={onShowLogs}
                            serviceInfo={serviceInfo}
                            onOpenCommunication={onOpenCommunication}
                            onOpenFormSelector={onOpenFormSelector}
                            originalReservation={reservation}
                            selectedSlotType={selectedSlotType}
                            setSelectedSlotType={handleSlotTypeChange}
                            rates={rates}
                            loading={savingReservation}
                            onSaveAndSendNoShowLink={handleSaveAndSendNoShowLink}
                        />
                    </Stack>
                }

                {
                    !chosenProfile && sector?.sectorType === SectorType.ACCOMMODATION &&
                    <Stack sx={{order: {xs: 2, md: 2}, flex: 1, overflowY: {xs: 'initial', md: 'auto'}, ...thinScrollbarSx}}>
                        <ReservationDetailsAccommodation
                            reservation={tmpReservation}
                            setReservation={setTmpReservation}
                            // availabilityForActiveReservation={availabilityForActiveReservation}
                            // availabilityQuery={availabilityQuery.isLoading}
                            onSave={(allowNotify) => {
                                // setOpenSaveDialog(true)
                                handleSave(allowNotify)
                            }}
                            onResend={() => {
                                setOpenResendDialog(true)
                            }}
                            onShowLogs={onShowLogs}
                            ageCategories={ageCategories}
                            onClose={onClose}
                            onShowFolios={onShowFolios}
                            serviceInfo={serviceInfo}
                            onOpenCommunication={onOpenCommunication}
                            onOpenFormSelector={onOpenFormSelector}
                            originalReservation={reservation}
                            rates={rates}
                            loading={savingReservation}
                            onSaveAndSendNoShowLink={handleSaveAndSendNoShowLink}
                        />
                    </Stack>
                }

                {
                    // Spa packages stay on the RESERVATION slot type, so without the package check staff
                    // never saw that the service was booked as part of a package.
                    !chosenProfile && (selectedSlotType === SlotType.SERIAL || isPackageMember) && (!!linkedReservations.length || !!linkedReservationsParam?.length) &&
                    (
                        <Stack sx={{order: {xs: 0, md: 3}}}>
                            <ReservationGroupInfo
                                reservation={tmpReservation}
                                linkedReservations={
                                    linkedReservations.length
                                        ? linkedReservations
                                        : linkedReservationsParam
                                }
                                onOpenReservation={onChangeReservation}
                                onUpdateLinkedReservationTable={handleUpdateLinkedReservationTable}
                            />
                        </Stack>
                    )
                }

            </Stack>
        )
    }


    // Warn (only while creating) when the chosen slot falls outside the resource's open availability.
    const isCreatingReservation = !!tmpReservation && !tmpReservation.id && selectedSlotType !== SlotType.BLOCK
    const availabilityResourceIds = tmpReservation?.resourceIds ?? []

    // Always fetch availability for the dialog's actual resource(s) and day while creating. This makes
    // the warning correct regardless of entry point (calendar drag, sidebar "+", list view), and it
    // reacts to the resource/time the user picks inside the dialog. The calendar's pre-fetched
    // `availabilityResults` is only used as an instant fallback until this fetch resolves.
    const shouldFetchAvailability = isCreatingReservation && availabilityResourceIds.length > 0
    const fetchedAvailabilityQ = useAvailabilityCalculationResults({
        queryKey: [
            'reservationDialogAvailability',
            availabilityResourceIds,
            tmpReservation?.expectedStartDate,
            tmpReservation?.expectedEndDate,
        ],
        entityIds: shouldFetchAvailability ? availabilityResourceIds : undefined,
        dateRangeFrom: tmpReservation?.expectedStartDate ? startOfDay(tmpReservation.expectedStartDate) : new Date(),
        dateRangeTo: tmpReservation?.expectedEndDate ? endOfDay(tmpReservation.expectedEndDate) : new Date(),
    })

    const effectiveAvailabilityResults = fetchedAvailabilityQ.data ?? availabilityResults ?? []

    // Only warn once the slot time is deliberate, so picking a resource alone (while the time is still
    // the default placeholder, e.g. from the sidebar "+") doesn't immediately flash the warning. The
    // time counts as deliberate when a resource was already attached at open (calendar drag / floorplan,
    // where the slot itself was chosen) or when the user changes the start time inside the dialog.
    const startTimeChanged =
        tmpReservation?.expectedStartDate?.getTime() !== reservation?.expectedStartDate?.getTime()
    const timeIsDeliberate = !!reservation?.resourceIds?.length || startTimeChanged

    const showOutsideAvailabilityWarning =
        isCreatingReservation &&
        timeIsDeliberate &&
        isReservationOutsideOpenAvailability(tmpReservation, effectiveAvailabilityResults)

    // Rendered just above the resource picker (inside the form grid) so it shares the same gaps as the
    // other fields, instead of floating at the very top of the dialog.
    const availabilityWarning = showOutsideAvailabilityWarning ? (
        <Alert severity="warning" variant="outlined">
            {t('This reservation is outside the open availability of the selected resource')}
        </Alert>
    ) : undefined

    return (
        <>

            <Dialog
                open={open}
                onClose={() => {
                    onClose?.()
                }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
                maxWidth={selectedSlotType === SlotType.BLOCK ? 'md' : 'lg'}
                TransitionProps={{onEntered: () => setContentReady(true)}}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', md: undefined },
                        // Wider than 'lg' (1200px): the guest sidebar takes 360px, the reservation keeps its room.
                        ...(selectedSlotType !== SlotType.BLOCK && {maxWidth: { xs: '100%', sm: 'min(1280px, calc(100% - 64px))' }}),
                        margin: { xs: 0, sm: 4 },
                        maxHeight: { xs: '100dvh', md: 'calc(100% - 64px)' },
                        // One height whatever the sidebar shows (search, guest, full profile), so the dialog never
                        // jumps; 900px at most on big screens. A block has no sidebar and keeps its content height.
                        ...(selectedSlotType !== SlotType.BLOCK && {height: { md: 'min(900px, calc(100% - 64px))' }}),
                        borderRadius: { xs: 0, sm: 1 },
                        // Read-only: block interaction with every autocomplete in the dialog
                        // (opening the dropdown, clearing, typing) while keeping the rest viewable.
                        ...(readOnly && {
                            '& .MuiAutocomplete-root': {
                                pointerEvents: 'none',
                                opacity: 0.6,
                            },
                        }),
                    }
                }}
            >
                {contentReady
                    ? renderContent()
                    : (
                        <Stack alignItems="center" justifyContent="center" sx={{flex: 1, minHeight: 360}}>
                            <CircularProgress/>
                        </Stack>
                    )}

                <ReservationFoliosDialog
                    open={showFolios}
                    onClose={() => setShowFolios(false)}
                    reservationId={reservation?.id}
                    profileId={reservation?.profileIds?.[0]}
                />

                <ReservationCommunicationDialog
                    open={showCommunication}
                    onClose={() => setShowCommunication(false)}
                    reservation={reservation}
                />

                <ReservationLogsDialog open={showLogs}
                                       onClose={() => setShowLogs(false)}
                                       reservationId={reservation?.id}/>

                <ReservationFormSelectorDialog
                    open={showFormSelector}
                    onClose={() => setShowFormSelector(false)}
                    sectorId={appData?.configuration?.sectorId}
                    profileId={reservation?.primaryProfileId || reservation?.profileIds?.[0]}
                />

                {/*{saveDialog}*/}
            </Dialog>

            <SimpleDialog
                open={confirmNoShowDialogOpen}
                onClose={() => setConfirmNoShowDialogOpen(false)}
                onCancel={() => setConfirmNoShowDialogOpen(false)}
                onConfirm={handleConfirmedSendNoShowLink}
                title={t('Send no-show link')}
                message={confirmNoShowEmail
                    ? t('The no-show link will be sent to {{email}}.', {email: confirmNoShowEmail})
                    : t('Are you sure you want to send the no-show link?')}
                confirmText={t('Send')}
                cancelText={t('Cancel')}
            />

            <Dialog open={noEmailDialogOpen} onClose={() => { setNoEmailDialogOpen(false); setNewEmail(''); }}>
                <DialogTitle>{t('Guest has no email')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <span>{t('The guest needs an email address to send a no-show link.')}</span>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            label={t('Email address')}
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setNoEmailDialogOpen(false); setNewEmail(''); }}>
                        {t('Cancel')}
                    </Button>
                    <Button onClick={handleAddEmailAndSend} variant="contained" disabled={!newEmail.trim()}>
                        {t('Add & Send')}
                    </Button>
                </DialogActions>
            </Dialog>

        </>

    )
}
export default ReservationDialog;
